import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useSelector } from 'react-redux'
import { getSelectedCustomerId } from '../selectors/customers'
import prepareHeaders from '../utils/prepareHeaders'

export const customerGraphResourcesApi = createApi({
  reducerPath: 'customerGraphResources',
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getCustomerServicePrincipals: builder.query({
      query: ({ customerId, params }) => ({
        url: `/customer/${customerId}/service-principals`,
        params,
      }),
    }),
    getCustomerWorkloadIdentities: builder.query({
      query: ({ customerId }) => ({
        url: `/customer/${customerId}/workload-identities`,
      }),
    }),
    getCustomerDirectoryRoleDefinitions: builder.query({
      query: ({ customerId }) => ({
        url: `/customer/${customerId}/directory-role-definitions`,
      }),
    }),
    // Microsoft 365 profile photo as a data URI, or null when the user has none.
    getCustomerUserPhoto: builder.query({
      query: ({ customerId, userId }) => ({
        url: `/customer/${customerId}/user-photo/${userId}`,
      }),
      transformResponse: (response) => response?.photo ?? null,
    }),
  }),
})

export const {
  useGetCustomerServicePrincipalsQuery,
  useLazyGetCustomerServicePrincipalsQuery,
  useLazyGetCustomerDirectoryRoleDefinitionsQuery,
  useGetCustomerWorkloadIdentitiesQuery,
  useGetCustomerUserPhotoQuery,
} = customerGraphResourcesApi

const EMPTY_WORKLOAD_IDENTITIES = []

// Live from Graph: the single-tenant applications the tenant owns, which are the only identities a
// workload identity Conditional Access policy can target. isError lets the form say the list
// couldn't be loaded rather than looking like the tenant has none.
export const useTenantWorkloadIdentities = (customerId) => {
  const { currentData, isError } = useGetCustomerWorkloadIdentitiesQuery({ customerId }, { skip: !customerId })
  return { servicePrincipals: currentData ?? EMPTY_WORKLOAD_IDENTITIES, isError }
}

export const useGetCustomerServicePrincipals = () => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetCustomerServicePrincipalsQuery({ customerId })
}
